import React, { useCallback, useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import ThreatDashboardSection from "../components/ThreatDashboard";
import { TitleRaceCard, EuropeRaceCard, RelegationCard } from "../components/RaceTables";
import { SeasonEndBanner, isBetweenSeasons } from "../components/SeasonEndBanner";
import NewsSignalAdmin from "../components/NewsSignalAdmin";
import EuropeSlotsAdmin, { EuropeSlotsBanner } from "../components/EuropeSlotsAdmin";
import TagLegendBanner from "../components/TagLegendBanner";
import WeeklyChallengeBanner from "../components/WeeklyChallengeBanner";
import { PlayerTag } from "../components/badges";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
    Lightning, TrendUp, TrendDown, Swap, Bandaids, Compass,
} from "@phosphor-icons/react";
import { getContext, getQualification, api, getMarketWatch } from "../lib/api";
import { Link } from "react-router-dom";
import { Siren } from "@phosphor-icons/react";

export default function Dashboard() {
    const [context, setContext] = useState(null);
    const [qualification, setQualification] = useState(null);
    const [loading, setLoading] = useState(true);
    const [contextWindow, setContextWindow] = useState(3);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [ctx, qual] = await Promise.all([
                getContext(contextWindow),
                getQualification().catch((e) => {
                    console.warn("Qualification fetch failed:", e?.response?.data?.detail || e?.message);
                    return null;
                }),
            ]);
            setContext(ctx);
            setQualification(qual);
        } catch (e) {
            console.error("Dashboard context load failed:", e);
            toast.error("Failed to load Premier League context");
        } finally {
            setLoading(false);
        }
    }, [contextWindow]);

    useEffect(() => { loadAll(); }, [loadAll]);

    return (
        <AppShell liveContext={context}>
            <div className="space-y-8" data-testid="dashboard-page">
                {/* Hero */}
                <section className="relative border border-white/10 bg-[#0a0a0a] overflow-hidden">
                    <div className="grain-overlay" />
                    <div
                        className="absolute inset-0 opacity-15"
                        style={{
                            backgroundImage:
                                "url(https://images.pexels.com/photos/14963646/pexels-photo-14963646.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            filter: "grayscale(100%) brightness(0.4)",
                        }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-transparent" />
                    <div className="relative p-6 md:p-10 flex flex-col gap-4 max-w-4xl">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-fa-cream flex items-center gap-2">
                            <Compass size={12} weight="fill" />
                            Decision Support · {qualification?.season || "Season 2025-26"}
                            {qualification?.as_of_gameweek ? ` · GW${qualification.as_of_gameweek}` : ""}
                        </div>
                        <h1 className="font-display font-black text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                            Premier League
                            <br />
                            <span className="text-fa-cream">Dashboard.</span>
                        </h1>
                        <p className="text-white/70 max-w-xl leading-relaxed">
                            Who is still alive? Who is in danger? Who is already safe? Tier-aware
                            probabilities, threat signals and live transfer momentum — all in one
                            place to inform your next FPL decision.
                        </p>
                        <p className="font-mono text-[11px] text-white/50">
                            Need to create challenges? Head to the{" "}
                            <a
                                href="/generator"
                                className="text-fa-yellow underline-offset-2 hover:underline"
                                data-testid="dashboard-to-generator-link"
                            >
                                Challenge Generator
                            </a>.
                        </p>
                    </div>
                </section>

                {/* Between-seasons: prominent banner right at the top */}
                <SeasonEndBanner context={context} />

                {/* Weekly Transfer Desk CTA — renders only when live */}
                <WeeklyChallengeBanner />

                {/* Race tables */}
                <section className="space-y-4">
                    <SectionHeading
                        title="Race Tables"
                        right={
                            <div className="flex items-center gap-2">
                                <Label className="font-mono text-[10px] uppercase tracking-widest text-white/50">
                                    Context window
                                </Label>
                                <Input
                                    type="number"
                                    min={1}
                                    max={10}
                                    value={contextWindow}
                                    onChange={(e) => setContextWindow(Number(e.target.value) || 1)}
                                    className="w-16 h-8 bg-black border-white/10 rounded-none font-mono text-center"
                                    data-testid="input-context-window"
                                />
                                <span className="font-mono text-[10px] text-white/40 uppercase tracking-widest">GW</span>
                            </div>
                        }
                    />
                    {loading ? (
                        <div className="text-white/40 font-mono text-xs">Loading race tables…</div>
                    ) : isBetweenSeasons(context) ? (
                        <SeasonEndBanner context={context} />
                    ) : qualification ? (
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                            <TitleRaceCard
                                className="lg:col-span-12"
                                rows={qualification.title_race}
                                asOfGameweek={qualification.as_of_gameweek}
                            />
                            <div className="lg:col-span-12 space-y-3">
                                <EuropeSlotsBanner europeSlots={context?.europe_slots} />
                                <EuropeRaceCard
                                    rows={qualification.europe_race}
                                    cutoffPoints={qualification.europe_cutoff_points}
                                    asOfGameweek={qualification.as_of_gameweek}
                                    rules={qualification.rules}
                                />
                            </div>
                            <RelegationCard
                                className="lg:col-span-12"
                                rows={qualification.relegation}
                                cutoffPoints={qualification.relegation_cutoff_points}
                                asOfGameweek={qualification.as_of_gameweek}
                            />
                        </div>
                    ) : (
                        <div className="text-white/40 font-mono text-xs">Qualification engine unavailable.</div>
                    )}
                </section>

                {/* Threat dashboard */}
                <ThreatDashboardSection window={contextWindow} />

                {/* Tag legend (one-time, dismissable) */}
                <TagLegendBanner />

                {/* News signal admin (auth-gated inside) */}
                <NewsSignalAdmin
                    teamChoices={[
                        ...(qualification?.title_race || []),
                        ...(qualification?.europe_race || []),
                        ...(qualification?.relegation || []),
                    ].map((r) => r.team_name)}
                />

                {/* Europe slots admin (cup-winner / UEFA-winner override) */}
                <EuropeSlotsAdmin
                    teamChoices={[
                        ...(qualification?.title_race || []),
                        ...(qualification?.europe_race || []),
                        ...(qualification?.relegation || []),
                    ].map((r) => r.team_name)}
                />

                {/* Hlídač varuje */}
                <HlidacWidget />

                {/* Poslední důležité přestupy */}
                <RecentTransfersWidget />

                {/* Transfer momentum */}
                <section>
                    <SectionHeading title="Transfer Momentum" />
                    {loading ? (
                        <div className="text-white/40 font-mono text-xs">Loading momentum…</div>
                    ) : context ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <TransferCard
                                label="Bandwagons"
                                icon={<TrendUp size={14} weight="fill" />}
                                color="#9EE8B4"
                                players={context.bandwagons}
                                field="in"
                            />
                            <TransferCard
                                label="Sell-offs"
                                icon={<TrendDown size={14} weight="fill" />}
                                color="#FF2A2A"
                                players={context.sell_offs}
                                field="out"
                            />
                        </div>
                    ) : null}
                </section>

                {/* Six-pointers + rivalries */}
                {context && (context.six_pointers?.length > 0 || context.rivalries?.length > 0) && (
                    <section>
                        <SectionHeading title="Marquee Fixtures" />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {context.six_pointers?.length > 0 && (
                                <FixturesCard label="Six-Pointers (by importance)" color="#FFF3C2" fixtures={context.six_pointers} />
                            )}
                            {context.rivalries?.length > 0 && (
                                <FixturesCard label="Rivalries" color="#FF2A2A" fixtures={context.rivalries} />
                            )}
                        </div>
                    </section>
                )}
            </div>
        </AppShell>
    );
}

// ---- Inline helpers (lifted from prior Dashboard) -------------------------

function HlidacWidget() {
    const [data, setData] = useState(null);
    useEffect(() => {
        getMarketWatch().then((d) => setData(d)).catch(() => setData({ traps: [], gems: [] }));
    }, []);
    const traps = (data?.traps || []).slice(0, 3);
    const gems = (data?.gems || []).slice(0, 3);
    if (!data || (traps.length === 0 && gems.length === 0)) return null;
    return (
        <section data-testid="dashboard-hlidac">
            <SectionHeading
                title="Hlídač varuje"
                right={<Link to="/hlidac" data-testid="dashboard-hlidac-link"
                             className="font-mono text-[10px] uppercase tracking-widest whitespace-nowrap hover:opacity-80" style={{ color: "#FF8800" }}>
                    <Siren size={11} weight="fill" className="inline mb-0.5 mr-1" />Otevřít Hlídače →</Link>}
            />
            <div className="grid md:grid-cols-2 gap-2">
                <div className="border border-red-400/30 bg-red-400/5 p-3 space-y-1.5" data-testid="dashboard-hlidac-traps">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-red-300">🚨 Pasti — komunita kupuje, data varují</div>
                    {traps.map((t) => (
                        <div key={t.fpl_id} className="font-mono text-[10px] flex items-baseline gap-2 flex-wrap">
                            <Link to={`/players/${t.fpl_id}`} className="font-display font-bold text-sm hover:text-red-300">{t.name}</Link>
                            <span className="text-white/40">{t.team} · £{t.price?.toFixed?.(1)}</span>
                            <span className="text-white/60 basis-full">{(t.reasons || [])[0]}</span>
                        </div>
                    ))}
                </div>
                <div className="border border-fa-mint/30 bg-fa-mint/5 p-3 space-y-1.5" data-testid="dashboard-hlidac-gems">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-fa-mint">💎 Gemy — trh je přehlíží</div>
                    {gems.map((g) => (
                        <div key={g.fpl_id} className="font-mono text-[10px] flex items-baseline gap-2 flex-wrap">
                            <Link to={`/players/${g.fpl_id}`} className="font-display font-bold text-sm hover:text-fa-mint">{g.name}</Link>
                            <span className="text-white/40">{g.team} · £{g.price?.toFixed?.(1)}</span>
                            <span className="text-white/60 basis-full">{(g.reasons || [])[0]}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function RecentTransfersWidget() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        api.get("/radar/recent-relevant?limit=5").then((r) => setRows(r.data.transfers || [])).catch(() => setRows([]));
    }, []);
    if (!rows || rows.length === 0) return null;
    const fee = (t) => t.fee_text || (t.amount_eur ? `€${Math.round(t.amount_eur / 1e6)}m` : t.transfer_type === "loan" ? "hostování" : "—");
    return (
        <section data-testid="dashboard-recent-transfers">
            <SectionHeading
                title="Poslední důležité přestupy"
                right={<Link to="/radar" data-testid="recent-transfers-radar-link"
                             className="font-mono text-[10px] uppercase tracking-widest text-white/45 hover:text-fa-yellow whitespace-nowrap">Incoming Radar →</Link>}
            />
            <div className="border border-white/10 bg-[#0d0d0d] divide-y divide-white/5">
                {rows.map((t) => (
                    <div key={t.row_id} className="px-4 py-2.5 flex items-center gap-3 flex-wrap" data-testid={`recent-transfer-${t.row_id}`}>
                        {t.player_image && <img src={t.player_image} alt="" className="w-7 h-7 rounded-full border border-white/15 object-cover" />}
                        <span className="font-display font-bold text-sm">{t.player_name}</span>
                        {t.position && <span className="font-mono text-[9px] uppercase px-1.5 py-0.5 border border-white/20 text-white/55">{t.position}</span>}
                        <span className="font-mono text-[10px] text-white/55">{t.from_team} → <b className="text-white/85">{t.to_team}</b></span>
                        <span className="ml-auto flex items-center gap-3">
                            <span className="font-mono text-[11px] font-bold text-fa-yellow tabular-nums">{fee(t)}</span>
                            <span className="font-mono text-[9px] text-white/40 tabular-nums">{t.date}</span>
                        </span>
                    </div>
                ))}
            </div>
        </section>
    );
}


function SectionHeading({ title, right }) {
    return (
        <div className="flex items-end justify-between mb-4 gap-4">
            <h2 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tight">
                {title}
            </h2>
            <div className="flex-1 h-px bg-white/10" />
            {right}
        </div>
    );
}

function FixturesCard({ label, color, fixtures }) {
    return (
        <div className="bg-[#121212] border border-white/10 p-4">
            <div
                className="font-mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5"
                style={{ color }}
            >
                <Swap size={14} weight="bold" />
                {label}
            </div>
            <div className="space-y-1.5">
                {fixtures?.length ? fixtures.slice(0, 6).map((f, i) => (
                    <div
                        key={`${f.gw}-${f.home_short || f.home}-${f.away_short || f.away}-${i}`}
                        className="flex items-center justify-between bg-black/60 border border-white/5 px-2 py-1.5"
                    >
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="font-mono text-[10px] text-white/40 shrink-0">GW{f.gw}</span>
                            <span className="font-display font-bold text-sm uppercase">{f.home_short || f.home}</span>
                            <span className="text-white/30 font-mono text-xs">vs</span>
                            <span className="font-display font-bold text-sm uppercase">{f.away_short || f.away}</span>
                        </div>
                        <ImportanceBadge tier={f.importance_tier} score={f.importance_score} />
                    </div>
                )) : <div className="text-white/30 text-xs italic">No fixtures detected</div>}
            </div>
        </div>
    );
}

function ImportanceBadge({ tier, score }) {
    if (!tier && !score) return null;
    const color = tier === "HIGH" ? "#FF2A2A" : tier === "MED" ? "#FFF3C2" : "#A1A1AA";
    return (
        <span
            className="font-mono text-[9px] font-bold uppercase tracking-widest px-1 py-0.5 leading-none border"
            style={{ color, borderColor: `${color}60`, background: `${color}10` }}
            title={`Importance ${score}/100`}
        >
            {tier || "LOW"} · {score ?? 0}
        </span>
    );
}

function TransferCard({ label, icon, color, players, field }) {
    return (
        <div className="bg-[#121212] border border-white/10 p-4">
            <div
                className="font-mono text-[10px] uppercase tracking-widest mb-3 flex items-center gap-1.5"
                style={{ color }}
            >
                {icon}
                {label}
                <a
                    href="/market"
                    className="ml-auto text-white/40 hover:text-fa-yellow transition-colors"
                    data-testid={`market-link-${field}`}
                    title="Open the full market page"
                >
                    Show all →
                </a>
            </div>
            <div className="space-y-2">
                {players?.length ? players.map((p) => (
                    <TransferRow key={p.id} player={p} direction={field} color={color} />
                )) : <div className="text-white/30 text-xs italic">No data</div>}
            </div>
        </div>
    );
}

const BUCKET_STYLE = {
    EARLY: { bg: "#FF2A2A", text: "#ffffff", label: "EARLY" },
    MID: { bg: "#FFF3C2", text: "#050505", label: "MID" },
    RISING: { bg: "#9DB7F5", text: "#050505", label: "RISING" },
    OVERCROWDED: { bg: "#A1A1AA", text: "#050505", label: "CROWDED" },
};

const AVAILABILITY_STYLE = {
    injured: { bg: "#FF2A2A", text: "#ffffff", label: "INJURED" },
    suspended: { bg: "#FF8800", text: "#050505", label: "SUSP" },
    doubtful: { bg: "#FFD600", text: "#050505", label: "DOUBT" },
    unavailable: { bg: "#A1A1AA", text: "#050505", label: "OUT" },
};

function AvailabilityBadge({ availability }) {
    if (!availability || availability.level === "available") return null;
    const style = AVAILABILITY_STYLE[availability.level] || AVAILABILITY_STYLE.unavailable;
    return (
        <span
            className="font-tag text-[9px] font-bold uppercase px-1 py-0.5 leading-none flex items-center gap-1"
            style={{ background: style.bg, color: style.text }}
            data-testid="availability-badge"
        >
            <Bandaids size={10} weight="fill" />
            {style.label}
            {availability.level === "doubtful" && availability.chance != null && (
                <span>{availability.chance}%</span>
            )}
        </span>
    );
}

function TransferRow({ player, direction, color }) {
    const bucket = BUCKET_STYLE[player.bucket] || BUCKET_STYLE.MID;
    const momentum = Math.round((player.momentum || 0) * 100);
    const sign = direction === "in" ? "+" : "-";
    const deltaK = Math.round((player.abs_delta || Math.abs(player.delta || 0)) / 1000);
    const isUnavailable = player.availability && player.availability.level !== "available";
    return (
        <div
            className={`bg-black/60 border p-2 ${isUnavailable ? "border-fa-red/40" : "border-white/5"}`}
            data-testid={`transfer-row-${player.id}`}
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-[10px] text-white/40 uppercase">{player.team}</span>
                    <span className="font-display font-bold text-sm uppercase truncate">{player.name}</span>
                    <span className="font-mono text-[10px] text-white/40">{player.position}</span>
                </div>
                <div className="flex items-center gap-1">
                    <AvailabilityBadge availability={player.availability} />
                    <span
                        className="font-tag text-[9px] font-bold uppercase px-1 py-0.5 leading-none"
                        style={{ background: bucket.bg, color: bucket.text }}
                    >
                        {bucket.label}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
                <span className="font-mono text-[10px] text-white/60 tabular-nums">OWN {player.ownership}%</span>
                <span className="font-mono text-[10px] font-bold tabular-nums" style={{ color }}>
                    {sign}{deltaK}k
                </span>
                <div className="flex-1 h-1 bg-white/5 relative">
                    <div className="h-1" style={{ width: `${momentum}%`, background: color }} />
                </div>
            </div>
            {player.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                    {player.tags.map((t) => <PlayerTag key={t} tag={t} />)}
                </div>
            )}
        </div>
    );
}
